import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import { FormInput } from 'app/components/Input/FormInput';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';

import {
  Body,
  ButtonEdit,
} from 'app/modules/system-settings/configuration/components/AutoLogoutTimeItemCard';
import { CRITICAL_LEVEL_SETTING_VALUE } from 'app/modules/system-settings/configuration/core/constants';
import {
  createSetting,
  getSetting,
  updateSetting,
} from 'app/modules/system-settings/configuration/core/request';
import {
  Setting,
  SettingRequest,
  SettingType,
} from 'app/modules/system-settings/configuration/core/type';

import { getResponseError } from 'app/helpers/errors';
import { handleBlockInvalidCharacters } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';

import { ReactComponent as EditICon } from 'app/assets/icons/edit-icon.svg';

import { useFormik } from 'formik';

const CriticalLevelSettingCard = () => {
  const isEdit = useBoolean(false);

  const { data: criticalLevel, isLoading: isLoadingCriticalLevel } = useQuery<any>({
    queryKey: ['criticalLevel'],
    queryFn: () => getSetting(SettingType.criticalLevel),
    onSuccess: (data) => {
      formik.setFieldValue(
        'parLevelValue',
        data?.value ?? CRITICAL_LEVEL_SETTING_VALUE.parLevelValue,
      );
    },
    select: (data) => {
      const systemSetting = data.find((item: Setting) => !item.organization);
      return systemSetting;
    },
  });

  const queryClient = useQueryClient();

  const parLevelValueFromServer =
    criticalLevel?.value ?? CRITICAL_LEVEL_SETTING_VALUE.parLevelValue;

  const handleClickEdit = () => {
    isEdit.setValue(true);
  };

  const formik = useFormik({
    initialValues: { parLevelValue: parLevelValueFromServer },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const request: SettingRequest = {
        type: SettingType.criticalLevel,
        name: 'critical_level',
        value: values.parLevelValue.toString(),
      };

      try {
        let response;
        const id = criticalLevel?.id;

        if (!id) {
          response = await createSetting(request);
          if (response) toast.success('Critical level created successfully');
        }

        if (id) {
          response = await updateSetting(Number(id), request);
          if (response) toast.success('Critical level updated successfully');
        }

        isEdit.setValue(false);
        queryClient.invalidateQueries({ queryKey: ['criticalLevel'] });
      } catch (error) {
        getResponseError(error);
      }
    },
  });

  const handleCancel = () => {
    isEdit.setValue(false);
    formik.resetForm();
  };

  const renderButton = () => {
    if (!isEdit.value) {
      return (
        <div className="d-flex">
          <ButtonEdit
            className="btn d-flex justify-content-center align-items-center"
            onClick={handleClickEdit}
          >
            <EditICon />
            <Typography
              variant="title-4"
              color="primary"
              style={{
                marginLeft: 8,
              }}
            >
              Edit
            </Typography>
          </ButtonEdit>
        </div>
      );
    }

    return (
      <ActionButton
        onSubmit={formik.handleSubmit}
        onCancel={handleCancel}
        isSubmitting={formik.isSubmitting}
        disableButton={formik.isSubmitting || !formik.isValid || !formik.dirty}
        labelButtonSubmit="Save"
        notMarginTop
      />
    );
  };

  return (
    <Body className="mb-7 p-7">
      <form
        id="kt_modal_organization_form"
        className="form"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className="d-flex align-items-center justify-content-between mb-8">
          <Typography variant="body-6" color="gray-700">
            Default Critical Level
          </Typography>

          {renderButton()}
        </div>

        <div className="d-flex align-items-center gap-2 mt-6 mw-250px w-100">
          <FormInput
            label="Par Level"
            rootClass="w-100"
            extraLabelClass="mb-1"
            placeholder="Par Level"
            formik={formik}
            field="parLevelValue"
            typeInput="number"
            onKeyDown={handleBlockInvalidCharacters}
            marginBottom={0}
            isDisable={!isEdit.value}
            autoFocus
            style={{ minWidth: '238px' }}
          />

          <Typography
            variant="body-5"
            className="mt-6"
            color={!isEdit.value ? 'neutral-6' : 'neutral-10'}
          >
            {Number(formik.values.parLevelValue) > 1 ? 'items' : 'item'}
          </Typography>
        </div>
      </form>

      {isLoadingCriticalLevel && <LoadingCard />}
    </Body>
  );
};

export default CriticalLevelSettingCard;
